import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleHelp,
  Clock3,
  ExternalLink,
  FileSearch2,
  FileText,
  Gavel,
  Info,
  Landmark,
  ListChecks,
  LoaderCircle,
  MessageCircleQuestion,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConfirmDialog, useToast } from "@tribunalea/ui";
import { CivitaAssistantLauncher } from "../components/CivitaAssistant.jsx";

const relationshipOptions = Object.freeze([
  {
    id: "actor",
    label: "Inicié el reclamo",
    description: "Soy quien presentó la demanda o inició la causa.",
  },
  {
    id: "defendant",
    label: "Me hicieron el reclamo",
    description: "Soy la persona contra quien se inició la causa.",
  },
  {
    id: "other",
    label: "Lo sigo por otro motivo",
    description: "Lo sigo por otra persona o tengo otro vínculo.",
  },
  {
    id: "unknown",
    label: "No lo sé",
    description: "Civita evitará atribuirme órdenes o plazos.",
  },
]);

const actionPresentation = Object.freeze({
  explicit_action: {
    label: "Aparece una acción concreta",
    title: "Hay algo que conviene revisar ahora",
    icon: AlertCircle,
  },
  possible_action: {
    label: "Necesita confirmación",
    title: "Puede haber algo para hacer",
    icon: Clock3,
  },
  no_action_found: {
    label: "Sin acción expresa encontrada",
    title: "No encontramos una acción expresa",
    icon: CheckCircle2,
  },
  unclear: {
    label: "No alcanza la información",
    title: "No queda claro si tenés que hacer algo",
    icon: CircleHelp,
  },
});

const relationshipLabels = Object.freeze({
  actor: "Iniciaste el reclamo",
  defendant: "Te hicieron el reclamo",
  other: "Lo seguís por otro motivo",
  unknown: "No sabemos qué parte sos",
});

const demoSearchResult = Object.freeze({
  id: "pjm-demo",
  cuij: "13-05482147-8",
  caratula: "PÉREZ, ANA C/ EMPRESA EJEMPLO S.A. P/ DAÑOS Y PERJUICIOS",
  numero: "405.218",
  organismoActual: "Primer Tribunal de Gestión Asociada",
  fuero: "Civil",
  provincia: "Mendoza",
  isFollowed: false,
  civitaCaseId: null,
});

const demoCase = Object.freeze({
  id: "seguimiento-ejemplo",
  demo: true,
  cuij: "13-05482147-8",
  number: "405.218",
  title: "PÉREZ, ANA C/ EMPRESA EJEMPLO S.A. P/ DAÑOS Y PERJUICIOS",
  court: "Primer Tribunal de Gestión Asociada",
  jurisdiction: "Civil",
  province: "Mendoza",
  relationship: "actor",
  relationshipName: "Ana Pérez",
  generation: 7,
  hasNewActivity: true,
  status: "ready",
  explanationStatus: "ready",
  lastActivityAt: "2026-09-10T12:00:00.000Z",
  officialUrl: null,
  sync: {
    details: { status: "ready" },
    intelligence: { status: "ready", phase: "completed", percentage: 100 },
  },
  explanation: {
    headline: "El tribunal pidió una aclaración antes de seguir",
    situation: "La causa sigue abierta. El tribunal revisó la presentación de la parte actora y pidió que aclare un dato del domicilio denunciado antes de avanzar con la notificación a la empresa.",
    summary: "La demanda fue presentada y el tribunal ya realizó una primera revisión. Todavía no aparece una respuesta de la empresa. El movimiento más reciente es un pedido del tribunal a la parte actora para completar información.",
    actionStatus: "explicit_action",
    actionSummary: "Como indicás que sos la parte actora, este pedido podría corresponderte. La actuación habla de completar el domicilio, pero esta vista no confirma si ya fuiste notificada ni desde cuándo correría un plazo.",
    actionItems: [
      {
        title: "Confirmar el domicilio informado",
        explanation: "El tribunal pidió que la parte actora aclare el domicilio de la empresa antes de ordenar la notificación.",
        dueDate: null,
        urgency: "soon",
        sources: [{ sourceId: "982103", title: "Previo a proveer, aclare domicilio", type: "Decreto", date: "2026-09-10T12:00:00.000Z", author: "Tribunal", hasDocument: true }],
      },
    ],
    latestUpdate: {
      title: "El tribunal pidió completar un dato",
      explanation: "Antes de seguir con la notificación, pidió que se aclare el domicilio denunciado para la empresa.",
      date: "2026-09-10T12:00:00.000Z",
      whyItMatters: "Hasta que ese dato quede aclarado, la notificación a la otra parte podría no avanzar.",
      sources: [{ sourceId: "982103", title: "Previo a proveer, aclare domicilio", type: "Decreto", date: "2026-09-10T12:00:00.000Z", author: "Tribunal", hasDocument: true }],
    },
    stage: {
      label: "Revisión inicial de la demanda",
      explanation: "El tribunal está controlando que la presentación tenga los datos necesarios para continuar. Eso no significa que haya decidido quién tiene razón.",
      confidenceNote: "La etapa se describe a partir de las actuaciones disponibles hasta el 10 de septiembre.",
      sources: [{ sourceId: "982103", title: "Previo a proveer, aclare domicilio", type: "Decreto", date: "2026-09-10T12:00:00.000Z", author: "Tribunal", hasDocument: true }],
    },
    recentEvents: [
      { title: "El tribunal pidió una aclaración", explanation: "Solicitó a la parte actora completar el domicilio de la empresa.", date: "2026-09-10T12:00:00.000Z", category: "court_decision", sources: [{ sourceId: "982103", title: "Previo a proveer, aclare domicilio", type: "Decreto", date: "2026-09-10T12:00:00.000Z", author: "Tribunal", hasDocument: true }] },
      { title: "Se presentó la demanda", explanation: "La parte actora ingresó el escrito con el que comenzó este reclamo.", date: "2026-09-03T12:00:00.000Z", category: "party_filing", sources: [{ sourceId: "981771", title: "Presentación de demanda", type: "Escrito", date: "2026-09-03T12:00:00.000Z", author: "Parte actora", hasDocument: true }] },
      { title: "Se creó el expediente", explanation: "El Poder Judicial registró la causa y le asignó este número.", date: "2026-09-02T12:00:00.000Z", category: "movement", sources: [{ sourceId: "981702", title: "Inicio de expediente", type: "Movimiento", date: "2026-09-02T12:00:00.000Z", author: "Poder Judicial", hasDocument: false }] },
    ],
    importantDates: [
      { date: "10 de septiembre de 2026", label: "Pedido de aclaración", explanation: "Es la fecha de la última decisión publicada. No confirma por sí sola cuándo fue notificada la parte actora.", status: "past", sources: [{ sourceId: "982103", title: "Previo a proveer, aclare domicilio", type: "Decreto", date: "2026-09-10T12:00:00.000Z", author: "Tribunal", hasDocument: true }] },
    ],
    people: [
      { name: "Ana Pérez", role: "Parte actora", explanation: "Es quien inició el reclamo según la información del expediente." },
      { name: "Empresa Ejemplo S.A.", role: "Parte demandada", explanation: "Es la empresa contra quien se inició el reclamo." },
    ],
    terms: [
      { term: "Parte actora", meaning: "La persona que inició el reclamo judicial." },
      { term: "Proveer", meaning: "Que el tribunal revise una presentación y decida cómo continúa." },
      { term: "Notificación", meaning: "La comunicación formal que informa una decisión o presentación." },
    ],
    questionsForProfessional: [
      { question: "¿Este pedido ya fue respondido?", reason: "La actuación muestra el pedido, pero no una respuesta posterior." },
      { question: "¿Fui notificada formalmente y hay un plazo en curso?", reason: "La fecha de publicación no permite calcular por sí sola un vencimiento." },
      { question: "¿Qué dato exacto del domicilio falta aclarar?", reason: "Permite responder de manera precisa al pedido del tribunal." },
    ],
    limitations: [
      "No vemos en estas actuaciones si la parte actora recibió una notificación por otro medio.",
      "No calculamos un vencimiento porque no aparece una fecha de recepción confirmada.",
    ],
    professionalHelpNote: "Confirmá este pedido y cualquier plazo con tu abogado o profesional. La explicación traduce la información publicada, pero no reemplaza la revisión del expediente completo ni una indicación profesional para tu situación.",
  },
  recentPjmActivities: [
    { sourceId: "982103", title: "Previo a proveer, aclare domicilio", type: "Decreto", date: "2026-09-10T12:00:00.000Z", author: "Tribunal", hasDocument: true },
    { sourceId: "981771", title: "Presentación de demanda", type: "Escrito", date: "2026-09-03T12:00:00.000Z", author: "Parte actora", hasDocument: true },
    { sourceId: "981702", title: "Inicio de expediente", type: "Movimiento", date: "2026-09-02T12:00:00.000Z", author: "Poder Judicial", hasDocument: false },
  ],
});

function formatDate(value, { includeTime = false } = {}) {
  if (!value) return "Fecha no informada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-AR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
}

function sourceKey(source) {
  return String(source?.sourceId || "");
}

function Sources({ sources = [], onSelect }) {
  if (!sources.length) return null;
  return (
    <div className="case-sources" aria-label="Fuentes del Poder Judicial">
      {sources.map((source) => (
        <button type="button" key={sourceKey(source)} onClick={() => onSelect(source)}>
          <FileText size={13} /> Fuente PJM · {formatDate(source.date)}
        </button>
      ))}
    </div>
  );
}

function ProcessingStep({ state, children, detail }) {
  return (
    <li className={`processing-step is-${state}`}>
      <span>{state === "done" ? <Check size={15} /> : state === "active" ? <LoaderCircle className="is-spinning" size={15} /> : null}</span>
      <div>{children}{detail && <small>{detail}</small>}</div>
    </li>
  );
}

function FollowCaseProcessing({ followedCase, onBack }) {
  const detailsReady = followedCase.sync?.details?.status === "ready";
  const intelligenceStatus = followedCase.sync?.intelligence?.status;
  const readingReady = ["ready", "incomplete"].includes(intelligenceStatus);
  const explaining = followedCase.status === "explaining" || followedCase.explanationStatus === "analyzing";
  const percentage = Number(followedCase.sync?.intelligence?.percentage || 0);
  return (
    <main className="civita-document-page case-page processing-view">
      <button className="text-button" type="button" onClick={onBack}><ArrowLeft size={16} /> Mis seguimientos</button>
      <section className="processing-card" aria-live="polite">
        <span className="processing-card__icon"><Landmark size={25} /></span>
        <span className="eyebrow">Conectado al Poder Judicial de Mendoza</span>
        <h1>Estamos preparando una explicación clara</h1>
        <p>Traemos las actuaciones oficiales y las ordenamos para contarte qué pasó sin lenguaje judicial innecesario.</p>
        <ol>
          <ProcessingStep state="done">Expediente encontrado en PJM</ProcessingStep>
          <ProcessingStep state={detailsReady ? "done" : "active"}>Trayendo actuaciones y documentos</ProcessingStep>
          <ProcessingStep state={readingReady ? "done" : detailsReady ? "active" : "pending"} detail={detailsReady && !readingReady && percentage ? `${percentage}% leído` : null}>Leyendo qué hizo el tribunal y cada parte</ProcessingStep>
          <ProcessingStep state={followedCase.status === "ready" ? "done" : explaining || readingReady ? "active" : "pending"}>Traduciendo el estado a palabras cotidianas</ProcessingStep>
        </ol>
        <small>Puede demorar unos minutos si el expediente tiene muchas actuaciones. Al terminar, vas a empezar desde arriba de la explicación.</small>
      </section>
    </main>
  );
}

function FollowCaseError({ followedCase, retrying, onRetry, onBack }) {
  return (
    <main className="civita-document-page case-page processing-view">
      <section className="processing-card is-error">
        <span className="processing-card__icon"><AlertCircle size={25} /></span>
        <span className="eyebrow">No pudimos terminar</span>
        <h1>El seguimiento necesita otro intento</h1>
        <p>{followedCase.error?.message || "No pudimos consultar o interpretar toda la información del expediente."}</p>
        <div className="button-row">
          <button className="primary-button" type="button" onClick={onRetry} disabled={retrying}>
            {retrying ? <LoaderCircle className="is-spinning" size={17} /> : <RefreshCw size={17} />} Reintentar
          </button>
          <button className="secondary-button" type="button" onClick={onBack}>Volver a mis seguimientos</button>
        </div>
      </section>
    </main>
  );
}

function FollowCaseResult({
  api,
  followedCase,
  refreshing,
  onBack,
  onDelete,
  onRefresh,
}) {
  const { showToast } = useToast();
  const activityRefs = useRef(new Map());
  const [attachments, setAttachments] = useState(null);
  const [openingDocument, setOpeningDocument] = useState("");
  const explanation = followedCase.explanation;
  const action = actionPresentation[explanation.actionStatus] || actionPresentation.unclear;
  const ActionIcon = action.icon;
  const conversationMetadata = useMemo(() => ({
    surface: "civita_case",
    civitaCaseId: followedCase.id,
    cuij: followedCase.cuij,
  }), [followedCase.cuij, followedCase.id]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [followedCase.id]);

  function selectSource(source) {
    const target = activityRefs.current.get(sourceKey(source));
    if (!target) {
      showToast({ message: "No pudimos ubicar esa actuación en el listado visible.", tone: "info" });
      return;
    }
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.classList.remove("is-highlighted");
    window.requestAnimationFrame(() => target.classList.add("is-highlighted"));
    window.setTimeout(() => target.classList.remove("is-highlighted"), 2_400);
  }

  async function openDocument(source, attachmentId = null) {
    if (followedCase.demo) {
      showToast({ message: "En el ejemplo no se abre un documento real.", tone: "info" });
      return;
    }
    const key = `${sourceKey(source)}:${attachmentId || "main"}`;
    setOpeningDocument(key);
    try {
      const result = await api.getCaseDocument(followedCase.id, {
        actId: sourceKey(source),
        attachmentId,
      });
      if (result.status === "select_attachment") {
        setAttachments({ source, items: result.attachments });
        return;
      }
      if (result.status !== "pdf") {
        showToast({ message: "PJM no tiene un PDF disponible para esta actuación.", tone: "info" });
        return;
      }
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.download = result.fileName;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setAttachments(null);
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
    } finally {
      setOpeningDocument("");
    }
  }

  return (
    <main className="civita-document-page case-page case-result-page">
      <header className="case-result-header">
        <button className="text-button" type="button" onClick={onBack}><ArrowLeft size={16} /> Mis seguimientos</button>
        <div className="case-result-actions">
          <button type="button" onClick={onRefresh} disabled={refreshing || followedCase.demo}><RefreshCw className={refreshing ? "is-spinning" : ""} size={16} /> Actualizar desde PJM</button>
          <button className="is-danger" type="button" onClick={onDelete}><Trash2 size={16} /> Dejar de seguir</button>
        </div>
      </header>

      <section className="case-identity">
        <div className="case-identity__mark"><Gavel size={25} /></div>
        <div>
          <span className="eyebrow">Seguimiento judicial</span>
          <h1>{followedCase.title}</h1>
          <div className="case-identity__meta">
            <span><b>CUIJ</b> {followedCase.cuij}</span>
            {followedCase.number && <span><b>Número</b> {followedCase.number}</span>}
            {followedCase.court && <span><b>Tribunal</b> {followedCase.court}</span>}
          </div>
        </div>
      </section>

      <div className="case-trust-strip">
        <span><Landmark size={16} /> Datos traídos de PJM</span>
        <span><UserRound size={16} /> {relationshipLabels[followedCase.relationship]}</span>
        <span><Clock3 size={16} /> Último dato: {formatDate(followedCase.lastActivityAt)}</span>
      </div>

      <section className="case-plain-hero">
        <span className="eyebrow">Tu situación, en pocas palabras</span>
        <h2>{explanation.headline}</h2>
        <p>{explanation.situation}</p>
        <div><Info size={17} /><span>{explanation.summary}</span></div>
      </section>

      <section className={`case-action-card is-${explanation.actionStatus}`}>
        <div className="case-action-card__icon"><ActionIcon size={25} /></div>
        <div>
          <span>{action.label}</span>
          <h2>{action.title}</h2>
          <p>{explanation.actionSummary}</p>
          {explanation.actionItems?.length > 0 && (
            <div className="case-action-list">
              {explanation.actionItems.map((item, index) => (
                <article key={`${item.title}-${index}`}>
                  <span>{index + 1}</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.explanation}</p>
                    {item.dueDate && <small><CalendarDays size={13} /> Fecha informada: {item.dueDate}</small>}
                    <Sources sources={item.sources} onSelect={selectSource} />
                  </div>
                </article>
              ))}
            </div>
          )}
          <div className="case-action-caution"><ShieldCheck size={17} /><span>Antes de actuar o dejar pasar una fecha, confirmalo con tu abogado o profesional.</span></div>
        </div>
      </section>

      <div className="case-summary-grid">
        {explanation.latestUpdate && (
          <section className="case-latest-card">
            <header><span className="section-icon"><Sparkles size={18} /></span><div><span className="eyebrow">Lo último que pasó</span><small>{formatDate(explanation.latestUpdate.date)}</small></div></header>
            <h2>{explanation.latestUpdate.title}</h2>
            <p>{explanation.latestUpdate.explanation}</p>
            <div><b>Por qué importa</b><span>{explanation.latestUpdate.whyItMatters}</span></div>
            <Sources sources={explanation.latestUpdate.sources} onSelect={selectSource} />
          </section>
        )}
        <section className="case-stage-card">
          <header><span className="section-icon"><ListChecks size={18} /></span><div><span className="eyebrow">En qué etapa está</span></div></header>
          <h2>{explanation.stage.label}</h2>
          <p>{explanation.stage.explanation}</p>
          {explanation.stage.confidenceNote && <small>{explanation.stage.confidenceNote}</small>}
          <Sources sources={explanation.stage.sources} onSelect={selectSource} />
        </section>
      </div>

      {explanation.recentEvents?.length > 0 && (
        <section className="case-section case-timeline">
          <header><span className="section-number">1</span><div><h2>Qué fue pasando</h2><p>Los movimientos importantes, empezando por el más reciente.</p></div></header>
          <ol>
            {explanation.recentEvents.map((event, index) => (
              <li key={`${event.title}-${event.date}-${index}`}>
                <div className="case-timeline__line"><span>{index + 1}</span></div>
                <article>
                  <time>{formatDate(event.date)}</time>
                  <h3>{event.title}</h3>
                  <p>{event.explanation}</p>
                  <Sources sources={event.sources} onSelect={selectSource} />
                </article>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="case-detail-grid">
        {explanation.importantDates?.length > 0 && (
          <section className="case-section case-date-section">
            <header><span className="section-number"><CalendarDays size={17} /></span><div><h2>Fechas para ubicarte</h2><p>Una fecha publicada no siempre es un vencimiento.</p></div></header>
            <div>{explanation.importantDates.map((item, index) => <article key={`${item.date}-${item.label}-${index}`}><time>{item.date}</time><h3>{item.label}</h3><p>{item.explanation}</p><Sources sources={item.sources} onSelect={selectSource} /></article>)}</div>
          </section>
        )}
        {explanation.people?.length > 0 && (
          <section className="case-section case-people-section">
            <header><span className="section-number"><UsersRound size={17} /></span><div><h2>Quién es quién</h2><p>Las personas y organismos mencionados.</p></div></header>
            <div>{explanation.people.map((person, index) => <article key={`${person.name}-${index}`}><span><UserRound size={16} /></span><div><h3>{person.name}</h3><b>{person.role}</b><p>{person.explanation}</p></div></article>)}</div>
          </section>
        )}
      </div>

      {explanation.terms?.length > 0 && (
        <section className="case-section case-glossary">
          <header><span className="section-number">2</span><div><h2>Palabras judiciales, explicadas</h2><p>Qué significan dentro de este seguimiento.</p></div></header>
          <dl>{explanation.terms.map((item, index) => <div key={`${item.term}-${index}`}><dt>{item.term}</dt><dd>{item.meaning}</dd></div>)}</dl>
        </section>
      )}

      {explanation.questionsForProfessional?.length > 0 && (
        <section className="case-section case-questions">
          <header><span className="section-number">3</span><div><h2>Preguntas para llevar a tu profesional</h2><p>Podés copiarlas tal cual para despejar lo que todavía falta.</p></div></header>
          <ol>{explanation.questionsForProfessional.map((item, index) => <li key={`${item.question}-${index}`}><span>{index + 1}</span><div><strong>{item.question}</strong><p>{item.reason}</p></div></li>)}</ol>
        </section>
      )}

      <section className="professional-note case-professional-note">
        <ShieldCheck size={22} />
        <div><h2>Usá esta explicación para llegar mejor preparado</h2><p>{explanation.professionalHelpNote}</p></div>
      </section>

      {explanation.limitations?.length > 0 && (
        <details className="case-limitations">
          <summary><CircleHelp size={16} /> Qué no pudimos confirmar <ChevronRight size={16} /></summary>
          <ul>{explanation.limitations.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>
        </details>
      )}

      <section className="case-official-section">
        <header>
          <div><span className="section-icon"><Landmark size={18} /></span><div><span className="eyebrow">Datos originales</span><h2>Actuaciones informadas por PJM</h2><p>Esta parte conserva el nombre oficial de cada movimiento para que puedas verificar la explicación.</p></div></div>
          {followedCase.officialUrl && <a href={followedCase.officialUrl} target="_blank" rel="noreferrer">Abrir consulta oficial <ExternalLink size={15} /></a>}
        </header>
        <div className="case-official-list">
          {followedCase.recentPjmActivities?.map((activity) => (
            <article key={sourceKey(activity)} ref={(node) => { if (node) activityRefs.current.set(sourceKey(activity), node); else activityRefs.current.delete(sourceKey(activity)); }}>
              <time>{formatDate(activity.date)}</time>
              <div><span>{activity.type || "Actuación"}</span><h3>{activity.title}</h3>{activity.author && <p>Informado por: {activity.author}</p>}</div>
              {activity.hasDocument && <button type="button" onClick={() => openDocument(activity)} disabled={Boolean(openingDocument)}>{openingDocument.startsWith(`${sourceKey(activity)}:`) ? <LoaderCircle className="is-spinning" size={15} /> : <FileText size={15} />} Abrir documento</button>}
            </article>
          ))}
        </div>
      </section>

      <p className="ai-disclaimer">Esta explicación fue generada con IA a partir de la información disponible en PJM y puede equivocarse. No reemplaza el asesoramiento profesional ni una notificación oficial.</p>

      <CivitaAssistantLauncher
        className="case-assistant"
        title="Preguntá sobre este expediente"
        description="Justiniano se abrirá sobre el seguimiento y buscará dentro de sus actuaciones."
        sourceLabel="Solo este expediente"
        conversationScope={`seguimiento:${followedCase.id}:v1`}
        conversationMetadata={conversationMetadata}
        welcome="Ya leí el seguimiento y sus actuaciones. Puedo explicarte un movimiento, buscar cuándo ocurrió algo o ayudarte a preparar una pregunta para tu profesional."
        suggestions={["¿Qué significa el último movimiento?", "¿Aparece algún plazo dirigido a mi parte?", "¿Qué debería preguntarle a mi abogado?"]}
        appContext={{
          route: "/seguir-expediente",
          activeEntity: { type: "civita_case", id: followedCase.id, cuij: followedCase.cuij, label: followedCase.title },
          caseFollow: {
            cuij: followedCase.cuij,
            title: followedCase.title,
            court: followedCase.court,
            relationship: followedCase.relationship,
            relationshipName: followedCase.relationshipName,
            explanation,
          },
        }}
      />

      {attachments && (
        <div className="case-attachment-backdrop" role="presentation" onMouseDown={() => !openingDocument && setAttachments(null)}>
          <section className="case-attachment-dialog" role="dialog" aria-modal="true" aria-labelledby="case-attachment-title" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" aria-label="Cerrar" onClick={() => setAttachments(null)}><X size={18} /></button>
            <span className="section-icon"><FileText size={19} /></span>
            <h2 id="case-attachment-title">Elegí qué documento abrir</h2>
            <p>Esta actuación tiene más de un archivo publicado.</p>
            <div>{attachments.items.map((item) => <button type="button" key={item.id} onClick={() => openDocument(attachments.source, String(item.id))} disabled={Boolean(openingDocument)}><FileText size={17} /><span><strong>{item.title || "Documento adjunto"}</strong>{item.date && <small>{formatDate(item.date)}</small>}</span><ExternalLink size={15} /></button>)}</div>
          </section>
        </div>
      )}
    </main>
  );
}

function RelationshipDialog({ result, relationship, relationshipName, busy, onChange, onNameChange, onClose, onConfirm }) {
  return (
    <div className="case-relationship-backdrop" role="presentation" onMouseDown={() => !busy && onClose()}>
      <section className="case-relationship-dialog" role="dialog" aria-modal="true" aria-labelledby="case-relationship-title" onMouseDown={(event) => event.stopPropagation()}>
        <button className="case-dialog-close" type="button" aria-label="Cerrar" onClick={onClose} disabled={busy}><X size={19} /></button>
        <span className="section-icon"><UserRound size={19} /></span>
        <span className="eyebrow">Antes de explicarlo</span>
        <h2 id="case-relationship-title">¿Desde qué lugar seguís esta causa?</h2>
        <p>Esto evita que te atribuyamos una orden o un plazo dirigido a otra persona. No lo deducimos del nombre del expediente.</p>
        <div className="case-relationship-summary"><small>Expediente elegido</small><strong>{result.caratula}</strong><span>{result.cuij}</span></div>
        <div className="case-relationship-options">
          {relationshipOptions.map((option) => <button className={relationship === option.id ? "is-selected" : ""} type="button" key={option.id} onClick={() => onChange(option.id)}><span>{relationship === option.id && <Check size={15} />}</span><div><strong>{option.label}</strong><small>{option.description}</small></div></button>)}
        </div>
        <label className="case-name-field"><span>¿De quién querés entender la situación? <small>Opcional</small></span><input value={relationshipName} maxLength={300} onChange={(event) => onNameChange(event.target.value)} placeholder="Por ejemplo: Ana Pérez" /></label>
        <button className="analyze-button" type="button" onClick={onConfirm} disabled={!relationship || busy}>{busy ? <LoaderCircle className="is-spinning" size={18} /> : <Landmark size={18} />}{busy ? "Conectando con PJM…" : "Empezar a seguirlo"}<ArrowRight size={18} /></button>
      </section>
    </div>
  );
}

export function FollowCasePage({ api, authEnabled }) {
  const { showToast } = useToast();
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingCases, setLoadingCases] = useState(authEnabled);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState([]);
  const [resultPage, setResultPage] = useState(null);
  const [relationshipResult, setRelationshipResult] = useState(null);
  const [relationship, setRelationship] = useState("unknown");
  const [relationshipName, setRelationshipName] = useState("");
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function selectCase(item) {
    const needsFullDetail = !item?.demo
      && item?.status === "ready"
      && !Array.isArray(item?.recentPjmActivities);
    setSelected(needsFullDetail ? { ...item, status: "processing" } : item);
    if (!item?.demo) window.history.replaceState({}, "", `/seguir-expediente?caso=${encodeURIComponent(item.id)}`);
  }

  function resetView() {
    setSelected(null);
    setDeleteOpen(false);
    window.history.replaceState({}, "", "/seguir-expediente");
  }

  useEffect(() => {
    if (!authEnabled) return undefined;
    let active = true;
    api.listCases().then((items) => {
      if (!active) return;
      setCases(items);
      const requestedId = new URLSearchParams(window.location.search).get("caso");
      const match = requestedId && items.find((item) => item.id === requestedId);
      if (match) selectCase(match);
    }).catch((error) => showToast({ message: error.message, tone: "danger" })).finally(() => active && setLoadingCases(false));
    return () => { active = false; };
  }, [api, authEnabled, showToast]);

  useEffect(() => {
    if (!selected || selected.demo || ["ready", "failed"].includes(selected.status)) return undefined;
    let active = true;
    let timer;
    async function poll() {
      try {
        let current = await api.getCase(selected.id);
        if (current.status === "explaining") current = await api.explainCase(current.id);
        if (!active) return;
        setSelected(current);
        setCases((items) => items.map((item) => item.id === current.id ? current : item));
        if (!["ready", "failed"].includes(current.status)) timer = window.setTimeout(poll, 2_300);
      } catch (error) {
        if (!active) return;
        if (error.code === "civita_case_not_ready") { timer = window.setTimeout(poll, 2_300); return; }
        setSelected((current) => current ? { ...current, status: "failed", error: { code: error.code, message: error.message } } : current);
      }
    }
    void poll();
    return () => { active = false; window.clearTimeout(timer); };
  }, [api, selected?.demo, selected?.id, selected?.status]);

  useEffect(() => {
    if (!authEnabled || !selected || selected.demo || selected.status !== "ready") return;
    void api.markCaseSeen(selected.id).then(() => {
      setSelected((current) => current?.id === selected.id ? { ...current, hasNewActivity: false } : current);
      setCases((items) => items.map((item) => item.id === selected.id ? { ...item, hasNewActivity: false } : item));
    }).catch(() => {});
  }, [api, authEnabled, selected?.demo, selected?.id, selected?.status]);

  async function search(event, page = 1) {
    event?.preventDefault();
    if (query.trim().length < 3 || searching) return;
    setSearching(true);
    setSearched(true);
    try {
      if (!authEnabled) {
        setResults([demoSearchResult]);
        setResultPage({ page: 1, totalPages: 1, totalItems: 1 });
        return;
      }
      const data = await api.searchCases(query, page);
      setResults(data.items);
      setResultPage(data);
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
      setResults([]);
    } finally { setSearching(false); }
  }

  async function beginFollow() {
    if (!relationshipResult || !relationship || creating) return;
    setCreating(true);
    try {
      if (!authEnabled) {
        setRelationshipResult(null);
        selectCase({ ...demoCase, relationship, relationshipName });
        return;
      }
      const created = await api.createCase({
        cuij: relationshipResult.cuij,
        relationship,
        relationshipName: relationshipName.trim(),
      });
      setCases((items) => [created, ...items.filter((item) => item.id !== created.id)]);
      setRelationshipResult(null);
      selectCase(created);
    } catch (error) { showToast({ message: error.message, tone: "danger" }); }
    finally { setCreating(false); }
  }

  async function refresh() {
    if (!selected || selected.demo || refreshing) return;
    setRefreshing(true);
    try {
      const current = await api.refreshCase(selected.id);
      setSelected(current);
      setCases((items) => items.map((item) => item.id === current.id ? current : item));
    } catch (error) { showToast({ message: error.message, tone: "danger" }); }
    finally { setRefreshing(false); }
  }

  async function retry() {
    if (!selected || selected.demo || retrying) return;
    setRetrying(true);
    try {
      const current = await api.retryCase(selected.id);
      setSelected(current);
      setCases((items) => items.map((item) => item.id === current.id ? current : item));
    } catch (error) { showToast({ message: error.message, tone: "danger" }); }
    finally { setRetrying(false); }
  }

  async function remove() {
    if (!selected || deleting) return;
    if (selected.demo) { setDeleteOpen(false); resetView(); return; }
    setDeleting(true);
    try {
      await api.deleteCase(selected.id);
      setCases((items) => items.filter((item) => item.id !== selected.id));
      resetView();
      showToast({ message: "Dejaste de seguir el expediente en Civita.", tone: "success" });
    } catch (error) { showToast({ message: error.message, tone: "danger" }); }
    finally { setDeleting(false); }
  }

  if (selected?.status === "ready" && selected.explanation) return (
    <><FollowCaseResult api={api} followedCase={selected} refreshing={refreshing} onBack={resetView} onDelete={() => setDeleteOpen(true)} onRefresh={refresh} />{deleteOpen && <ConfirmDialog title="¿Dejar de seguir este expediente?" description="Se quitará de Civita junto con las conversaciones asociadas. No se modifica ni se elimina nada del Poder Judicial." confirmLabel="Dejar de seguir" busy={deleting} onClose={() => !deleting && setDeleteOpen(false)} onConfirm={remove} />}</>
  );
  if (selected?.status === "failed") return <FollowCaseError followedCase={selected} retrying={retrying} onRetry={retry} onBack={resetView} />;
  if (selected) return <FollowCaseProcessing followedCase={selected} onBack={resetView} />;

  return (
    <main className="civita-document-page case-page case-start-page">
      <header className="upload-heading case-start-heading">
        <span className="eyebrow">Seguir mi expediente</span>
        <h1>Entendé qué está pasando,<br />sin hablar “judicial”.</h1>
        <p>Buscá tu causa en el Poder Judicial de Mendoza. Civita trae sus movimientos, separa qué hizo cada persona y te explica lo importante con palabras claras.</p>
      </header>

      <section className="case-search-card">
        <div className="case-search-card__intro"><span><Landmark size={21} /></span><div><h2>Buscar en PJM</h2><p>La consulta se hace sobre la información pública del Poder Judicial de Mendoza.</p></div></div>
        <form onSubmit={search}>
          <label htmlFor="case-search">CUIJ, número o nombre de la causa</label>
          <div><Search size={20} /><input id="case-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Por ejemplo: 13-05482147-8 o Pérez c/ Empresa" autoComplete="off" /><button type="submit" disabled={query.trim().length < 3 || searching}>{searching ? <LoaderCircle className="is-spinning" size={18} /> : <Search size={18} />}{searching ? "Buscando…" : "Buscar"}</button></div>
          <small><CircleHelp size={14} /> El CUIJ es el número con formato 00-00000000-0. Suele aparecer en una cédula, notificación o mensaje de tu abogado.</small>
        </form>

        {searched && !searching && (
          <div className="case-search-results">
            <header><strong>{results.length ? `${resultPage?.totalItems || results.length} resultado${(resultPage?.totalItems || results.length) === 1 ? "" : "s"}` : "No encontramos resultados"}</strong>{results.length > 0 && <span>Revisá el nombre y el tribunal antes de elegir.</span>}</header>
            {results.length ? <div>{results.map((result) => <article key={result.cuij}><span className="case-result-icon"><Gavel size={19} /></span><div><h3>{result.caratula}</h3><p><b>CUIJ</b> {result.cuij}{result.numero && <> · <b>Número</b> {result.numero}</>}</p><small>{result.organismoActual || "Tribunal no informado"}{result.fuero ? ` · ${result.fuero}` : ""}</small></div><button type="button" onClick={() => { if (result.isFollowed && result.civitaCaseId) { const existing = cases.find((item) => item.id === result.civitaCaseId); if (existing) selectCase(existing); else api.getCase(result.civitaCaseId).then(selectCase).catch((error) => showToast({ message: error.message, tone: "danger" })); } else { setRelationship("unknown"); setRelationshipName(""); setRelationshipResult(result); } }}>{result.isFollowed ? "Abrir seguimiento" : "Elegir"}<ChevronRight size={16} /></button></article>)}</div> : <p className="case-no-results">Probá con el CUIJ completo. Si buscaste por un nombre, revisá que esté escrito como aparece en la notificación.</p>}
            {resultPage?.totalPages > 1 && <div className="case-pagination"><button type="button" disabled={resultPage.page <= 1} onClick={() => search(null, resultPage.page - 1)}>Anterior</button><span>Página {resultPage.page} de {resultPage.totalPages}</span><button type="button" disabled={resultPage.page >= resultPage.totalPages} onClick={() => search(null, resultPage.page + 1)}>Siguiente</button></div>}
          </div>
        )}
      </section>

      <section className="case-how-it-works">
        <header><span className="eyebrow">Qué vas a ver</span><h2>Del movimiento oficial a una explicación útil</h2></header>
        <div>
          <article><span>1</span><div><strong>Qué pasó</strong><p>Quién presentó algo o qué decidió el tribunal.</p></div></article>
          <article><span>2</span><div><strong>Qué significa</strong><p>La etapa y los términos, explicados desde cero.</p></div></article>
          <article><span>3</span><div><strong>Qué revisar ahora</strong><p>Órdenes, fechas y dudas que conviene confirmar.</p></div></article>
        </div>
      </section>

      <section className="case-history">
        <header><div><span className="eyebrow">Tus seguimientos</span><h2>Expedientes que estás siguiendo</h2></div>{!authEnabled && <button className="example-button" type="button" onClick={() => selectCase(demoCase)}>Ver un ejemplo completo</button>}</header>
        {loadingCases ? <div className="history-loading"><LoaderCircle className="is-spinning" size={18} /> Buscando tus seguimientos…</div> : cases.length ? <div className="case-history-list">{cases.map((item) => <button type="button" key={item.id} onClick={() => selectCase(item)}><span className="case-history-icon"><Gavel size={18} /></span><div><span>{item.hasNewActivity && <b>Hay novedades</b>}{!item.hasNewActivity && item.status !== "ready" && <b className="is-processing">Actualizando</b>}</span><h3>{item.title}</h3><p>{item.cuij}{item.court ? ` · ${item.court}` : ""}</p><small>Último dato: {formatDate(item.lastActivityAt)}</small></div><ChevronRight size={18} /></button>)}</div> : <div className="case-empty-history"><FileSearch2 size={24} /><div><strong>Todavía no seguís ningún expediente</strong><p>Cuando elijas uno, aparecerá acá y podremos avisarte visualmente si PJM publica nueva información.</p></div></div>}
      </section>

      <p className="case-start-disclaimer"><ShieldCheck size={15} /> Civita no modifica el expediente ni presenta escritos. Solo consulta y explica la información disponible.</p>

      {relationshipResult && <RelationshipDialog result={relationshipResult} relationship={relationship} relationshipName={relationshipName} busy={creating} onChange={setRelationship} onNameChange={setRelationshipName} onClose={() => setRelationshipResult(null)} onConfirm={beginFollow} />}
    </main>
  );
}
